import React, { useState, useEffect } from 'react';
import { PaymentHistory as PaymentHistoryType } from '../../types/subscription';
import { subscriptionApi } from '../../services/subscription.service';

interface PaymentHistoryProps {
  onRefresh: () => void;
}

export const PaymentHistory: React.FC<PaymentHistoryProps> = ({ onRefresh }) => {
  const [payments, setPayments] = useState<PaymentHistoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentHistory();
  }, []);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      const data = await subscriptionApi.getPaymentHistory();
      setPayments(data);
      setError(null);
    } catch (err) {
      setError('Failed to load payment history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (paymentId: string) => {
    try {
      const blob = await subscriptionApi.downloadInvoice(paymentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Failed to download invoice');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return '✓';
      case 'failed':
        return '✗';
      case 'pending':
        return '⏱';
      default:
        return '•';
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Payment History</h2>
        <button
          onClick={loadPaymentHistory}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-medium transition"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!loading && payments.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No payment history available</p>
        </div>
      )}

      {!loading && payments.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b-2 border-gray-300">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Date</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Amount</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, idx) => (
                <tr
                  key={payment.id}
                  className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}
                >
                  <td className="px-6 py-4 text-sm text-gray-800">{formatDate(payment.date)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                    ₹
                    {payment.amount.toFixed(2)}
                    {payment.currency && payment.currency !== 'INR' && ` ${payment.currency}`}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                        payment.status
                      )}`}
                    >
                      <span>{getStatusIcon(payment.status)}</span>
                      <span className="capitalize">{payment.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-600">
                    {payment.stripePaymentIntentId?.substring(0, 20)}...
                  </td>
                  <td className="px-6 py-4 text-right">
                    {payment.invoiceUrl && payment.status === 'success' && (
                      <button
                        onClick={() => handleDownloadInvoice(payment.id)}
                        className="text-blue-500 hover:text-blue-700 font-medium text-sm transition"
                      >
                        Download Invoice
                      </button>
                    )}
                    {!payment.invoiceUrl && (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Card */}
      {!loading && payments.length > 0 && (
        <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-gray-600 text-sm">Total Paid</p>
              <p className="text-2xl font-bold text-gray-800">
                ₹{payments
                  .filter((p) => p.status === 'success')
                  .reduce((sum, p) => sum + p.amount, 0)
                  .toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Successful Payments</p>
              <p className="text-2xl font-bold text-green-600">
                {payments.filter((p) => p.status === 'success').length}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Failed Payments</p>
              <p className="text-2xl font-bold text-red-600">
                {payments.filter((p) => p.status === 'failed').length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;


